import {
  Activity,
  BadgePercent,
  Building2,
  Cable,
  CircleDollarSign,
  Coins,
  CreditCard,
  FileChartColumn,
  FileSignature,
  FileText,
  Layers,
  LayoutDashboard,
  LayoutList,
  ListTodo,
  Megaphone,
  MessageSquare,
  Palette,
  Percent,
  Presentation,
  Receipt,
  ScrollText,
  Settings2,
  Sparkles,
  Ticket,
  Users,
  Video,
  Wallet,
  type LucideIcon,
} from "lucide-react"

/** Счётчики из inertia_share, которые показываются бейджем у пункта и в «Требует внимания». */
export type AdminMenuCounters = {
  pending_system_prompts_count?: number
  pending_payment_orders_count?: number
}

export type AdminMenuItem = {
  href: string
  labelKey: string
  icon: LucideIcon
  /** Синонимы через пробел для поиска в панели и ⌘K. */
  keywords: string
  badge?: { prop: keyof AdminMenuCounters; tone: "amber" | "danger" }
  /** Не Inertia-страница: переход полной загрузкой. */
  fullPage?: boolean
  /** Свой признак активности, когда URL раздела не начинается с href. */
  match?: (url: string) => boolean
}

export type AdminMenuGroup = { id: string; titleKey: string; items: AdminMenuItem[] }

const item = (
  href: string,
  labelKey: string,
  icon: LucideIcon,
  keywords = "",
  extra: Partial<AdminMenuItem> = {},
): AdminMenuItem => ({ href, labelKey, icon, keywords, ...extra })

const group = (id: string, titleKey: string, items: AdminMenuItem[]): AdminMenuGroup => ({ id, titleKey, items })

const BILLING = "/platform/billing"

// Компанейские URL биллинга расходятся по пунктам: /companies/:id/price-list — прайс-лист,
// /companies/:id/accounts/:id — выписка (пункт «Счета»), голый /companies/:id — дашборд.
const billingCompany = (url: string) => url.startsWith(`${BILLING}/companies/`)

/** Три колонки панели «Администрирование», в каждой — группы разделов платформы. */
export const ADMIN_MENU_COLUMNS: AdminMenuGroup[][] = [
  [
    group("platform", "shell.sidebar.platform", [
      item("/platform/companies", "shell.sidebar.companies", Building2, "studios"),
      item("/platform/users", "shell.sidebar.users", Users, "аккаунты accounts"),
      item("/platform/projects", "shell.sidebar.projects", LayoutList),
    ]),
    group("monitoring", "shell.monitoring.section", [
      item("/platform/monitoring/generations", "shell.monitoring.generations", Activity, "ошибки логи errors logs"),
      item("/platform/monitoring/dialogs", "shell.monitoring.dialogs", MessageSquare, "диалоги ассистент dialogs"),
      item("/jobs", "shell.monitoring.queueJobs", ListTodo, "jobs", { fullPage: true }),
    ]),
    group("catalogs", "shell.sidebar.catalogs", [
      item("/studio/style-catalog", "shell.studio.styleCatalog", Palette, "styles"),
      item("/studio/cameras", "shell.studio.cameras", Video, "ракурсы"),
      item("/studio/skills", "shell.studio.skills", Sparkles, "навыки"),
      item("/studio/presentations", "shell.studio.presentations", Presentation),
    ]),
  ],
  [
    group("billing", "shell.billing.section", [
      item(`${BILLING}/dashboard`, "shell.billing.dashboard", LayoutDashboard, "выручка revenue", {
        match: (url) =>
          url.startsWith(`${BILLING}/dashboard`) ||
          (billingCompany(url) && !url.includes("/price-list") && !url.includes("/accounts")) ||
          url.startsWith(`${BILLING}/projects/`),
      }),
      item(`${BILLING}/usage`, "shell.billing.usage", Receipt, "списания usage"),
      item(`${BILLING}/accounts`, "shell.billing.accounts", Wallet, "балансы выписки balances", {
        match: (url) => url.startsWith(`${BILLING}/accounts`) || (billingCompany(url) && url.includes("/accounts")),
      }),
      item(`${BILLING}/invoices`, "shell.billing.invoices", FileText, "оплата платёжки invoices", {
        badge: { prop: "pending_payment_orders_count", tone: "amber" },
      }),
      item(`${BILLING}/proposals`, "shell.billing.proposals", FileSignature, "кп коммерческое предложение proposals"),
      item(
        `${BILLING}/spend-reports`,
        "shell.billing.spendReports",
        FileChartColumn,
        "сводка отчёт расходы spend report pdf",
      ),
      item(`${BILLING}/online-payments`, "shell.billing.onlinePayments", CreditCard, "эквайринг карты payments"),
    ]),
    group("tariffs", "shell.billing.tariffs", [
      item(`${BILLING}/plans`, "shell.billing.plans", BadgePercent, "подписки"),
      item(`${BILLING}/promotions`, "shell.billing.promotions", Percent, "скидки"),
      item(`${BILLING}/promo-codes`, "shell.billing.promoCodes", Ticket),
      item(`${BILLING}/price-list`, "shell.billing.priceList", CircleDollarSign, "цены prices", {
        match: (url) => url.startsWith(`${BILLING}/price-list`) || url.includes("/price-list"),
      }),
      item(`${BILLING}/cost-rates`, "shell.billing.costRates", Coins, "cost rates ставки"),
      item(`${BILLING}/supplier`, "shell.billing.supplier", Building2, "supplier"),
    ]),
  ],
  [
    group("generation", "shell.adminGroups.generation", [
      item("/studio/generation-models", "shell.studio.generationModels", Layers, "модели models"),
      item("/studio/provider-connections", "shell.studio.providerConnections", Cable, "api ключи keys"),
      item("/studio/system-prompts", "shell.studio.systemPrompts", FileText, "system prompts системные", {
        badge: { prop: "pending_system_prompts_count", tone: "danger" },
      }),
    ]),
    group("service", "shell.adminGroups.service", [
      item("/studio/platform-config", "shell.studio.platformConfig", Settings2, "настройки config"),
      item("/studio/agreements", "shell.studio.agreements", ScrollText, "оферта"),
      item("/studio/splash-announcements", "shell.studio.splashAnnouncements", Megaphone),
    ]),
  ],
]

export const ADMIN_MENU_GROUPS: AdminMenuGroup[] = ADMIN_MENU_COLUMNS.flat()

export const ADMIN_MENU_ITEMS: AdminMenuItem[] = ADMIN_MENU_GROUPS.flatMap((g) => g.items)

export function adminMenuItemActive(menuItem: AdminMenuItem, url: string): boolean {
  return menuItem.match ? menuItem.match(url) : url.startsWith(menuItem.href)
}

/** Текущий раздел администрирования и его группа; null — страница не из админки. */
export function findAdminMenuItem(url: string): { item: AdminMenuItem; group: AdminMenuGroup } | null {
  for (const g of ADMIN_MENU_GROUPS) {
    const found = g.items.find((candidate) => adminMenuItemActive(candidate, url))
    if (found) return { item: found, group: g }
  }
  return null
}
